import { useState } from "react";
import { Box, Grid2, IconButton, Link, Paper, Typography } from "@mui/material";
import TranslateIcon from "@mui/icons-material/Translate";
import { BrandBatchSettings, colorByStatus, Job, JobStatus, LanguageCode, User } from "../../../pages/playground/types";
import { useGo } from "@refinedev/core";
import TranslationsDialog from "./translations-dialog";

export default function JobListJobItem({
  job,
  jobsLength,
  columnFilter,
  statusFilter,
  hasGdriveFolder,
  user,
  batchSettings,
  laraLanguages,
  laraInstructions,
  laraStyle,
}: {
  job: Job;
  jobsLength: number;
  columnFilter: string | null;
  statusFilter: string | null;
  hasGdriveFolder: boolean;
  user?: User;
  batchSettings?: BrandBatchSettings;
  laraLanguages?: LanguageCode[];
  laraInstructions?: string[];
  laraStyle?: string;
}) {
  const go = useGo();
  const [translationsDialogOpen, setTranslationsDialogOpen] = useState(false);

  // Show translations button for completed jobs when:
  // 1. User is admin, manager, or client-related
  // 2. Batch has lara_languages configured
  const showTranslationsButton =
    (user?.isAdmin || user?.isManager || user?.isClientRelated) &&
    laraLanguages &&
    laraLanguages.length > 0 &&
    job.status === JobStatus.COMPLETED;

  // Button should be green when translations are done, blinking/orange when pending
  const translationsDone = job.has_ai_translations === true;

  const isJustCreated = !job.images && !job.missing_images;
  const isMissingImages = job.missing_images;

  const renderStatus = () => {
    if (isJustCreated) {
      return (
        <Typography variant="body2" sx={{ textAlign: "center", mb: 2 }}>
          {!hasGdriveFolder
            ? "Images not yet uploaded"
            : "Recently created SKU"}
        </Typography>
      );
    }

    if (isMissingImages) {
      return (
        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            mb: 2,
            color: "error.main",
          }}
        >
          Missing or invalid images
        </Typography>
      );
    }

    return (
      <>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "100%",
            background:
              "linear-gradient(to top, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0) 100%)",
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            height: "100%",
            width: "100%",
            backgroundColor: "#f3f3f3",
          }}
        >
          <JobImage thumbnails={job.thumbnails} />
        </Box>
      </>
    );
  };

  const params = new URLSearchParams();
  if (columnFilter) params.set("column", columnFilter);
  if (statusFilter) params.set("status", statusFilter);
  const queryString = params.toString();
  const jobUrl = `/job/${job.id}${queryString ? `?${queryString}` : ""}`;

  return (
    <Grid2
      size={{
        xs: jobsLength <= 2 ? 6 : 6,
        sm: jobsLength <= 2 ? 6 : 6,
        md: jobsLength <= 2 ? 6 : 4,
        lg: jobsLength <= 2 ? 6 : 3,
      }}
    >
      <Link
        href={jobUrl}
        underline="none"
        color="inherit"
        sx={{
          display: "block",
          height: "100%",
        }}
        onClick={(e) => {
          // Only prevent default for regular clicks (not middle-click or ctrl+click)
          if (!e.ctrlKey && !e.metaKey && e.button === 0) {
            e.preventDefault();
            go({
              to: {
                resource: "job",
                action: "show",
                id: job.id,
              },
              query: {
                ...(params.size > 0 ? Object.fromEntries(params.entries()) : {}),
              },
            });
          }
        }}
      >
        <Paper
          elevation={1}
          sx={{
            cursor: "pointer",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            transition: "transform 0.2s, box-shadow 0.2s",
            "&:hover": {
              transform: "translateY(-8px)",
            },
          }}
        >
        <Box
          sx={{
            flexGrow: 1, // Take all available space
            height: 256,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f8f9fa",
            position: "relative", // For positioning elements
            overflow: "hidden",
            borderTop: `4px solid ${colorByStatus[job.status]}`,
          }}
        >
          {/* Master ID tag */}
          <Box
            sx={{
              position: "absolute",
              top: 12,
              ...(job.is_editor || job.is_reviewer
                ? { left: 12 }
                : {
                    left: "50%",
                    transform: "translateX(-50%)",
                  }),
              zIndex: 2,
              backdropFilter: "blur(10px)",
              backgroundColor: "#f8f9fa",
              boxShadow: "0 1px 3px rgba(0,0,0,0.5)",
              px: 1,
              py: 0.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: 12,
                letterSpacing: 0.5,
                color: "text.primary",
              }}
            >
              {job.master_id}
            </Typography>
          </Box>

          {/* Translations button for client users on completed jobs */}
          {showTranslationsButton && (
            <IconButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setTranslationsDialogOpen(true);
              }}
              size="small"
              sx={{
                position: "absolute",
                top: 10,
                right: job.is_editor || job.is_reviewer ? 100 : 12,
                zIndex: 3,
                backgroundColor: translationsDone ? "success.main" : "warning.main",
                color: "white",
                boxShadow: "0 1px 3px rgba(0,0,0,0.5)",
                width: 28,
                height: 28,
                "&:hover": {
                  backgroundColor: translationsDone ? "success.dark" : "warning.dark",
                },
                ...(!translationsDone && {
                  animation: "pulse 1.5s infinite",
                  "@keyframes pulse": {
                    "0%": {
                      opacity: 1,
                      transform: "scale(1)",
                    },
                    "50%": {
                      opacity: 0.7,
                      transform: "scale(1.05)",
                    },
                    "100%": {
                      opacity: 1,
                      transform: "scale(1)",
                    },
                  },
                }),
              }}
            >
              <TranslateIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}

          {(job.is_editor || job.is_reviewer) && (
            <Box
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                zIndex: 2,
                backgroundColor: job.is_reviewer
                  ? "secondary.main"
                  : "primary.main",
                boxShadow: "0 1px 3px rgba(0,0,0,0.5)",
                px: 1,
                py: 0.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: 12,
                  letterSpacing: 0.5,
                  color: job.is_reviewer
                    ? "secondary.contrastText"
                    : "primary.contrastText",
                }}
              >
                {job.is_editor && !job.is_reviewer && "Copywriter"}
                {job.is_reviewer && !job.is_editor && "Reviewer"}
                {job.is_editor && job.is_reviewer && "Copywriter & Reviewer"}
              </Typography>
            </Box>
          )}

          {renderStatus()}

          <Box
            sx={{
              position: "absolute",
              bottom: 12,
              width: "100%",
              padding: "0 1rem",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              zIndex: 2,
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 1,
                py: 0.5,
                backgroundColor: colorByStatus[job.status],
                boxShadow: "0 1px 3px rgba(0,0,0,0.5)",
                color: "white",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: 10,
                  fontWeight: 500,
                  textTransform: "uppercase",
                  color: "white",
                }}
              >
                {job.status
                  .split("_")
                  .map(
                    (word) =>
                      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                  )
                  .join(" ")}
              </Typography>
            </Box>

            {job.garment_type && job.garment_type !== "N/A" && (
              <Box
                sx={{
                  color: "black",
                  px: 1,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.5)",
                  py: 0.5,
                  backdropFilter: "blur(10px)",
                  backgroundColor: "white",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  {job.garment_type}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
      </Link>

      {/* Translations dialog */}
      <TranslationsDialog
        open={translationsDialogOpen}
        onClose={() => setTranslationsDialogOpen(false)}
        jobId={job.id}
        masterId={job.master_id}
        availableLanguages={laraLanguages}
        isAdmin={user?.isAdmin}
        batchSettings={batchSettings}
        batchInstructions={laraInstructions}
        batchStyle={laraStyle}
      />
    </Grid2>
  );
}

const JobImage = ({ thumbnails }: { thumbnails: null | string[] }) => {
  if (!thumbnails || thumbnails.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Loading optimized images
        </Typography>
      </Box>
    );
  }

  // Optimize grid layout for performance
  const getGridLayout = (imageCount: number) => {
    if (imageCount <= 1) return { rows: 1, cols: 1 };
    if (imageCount <= 2) return { rows: 1, cols: 2 };
    if (imageCount <= 4) return { rows: 2, cols: 2 };
    // Use a consistent 2x2 grid for better performance
    return { rows: 2, cols: 3 };
  };

  // Limit to maximum 4 images for better performance, but keep indicator for more
  const displayImages = thumbnails?.slice(0, 6);
  const { rows, cols } = getGridLayout(displayImages.length);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "grid",
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
      }}
    >
      {displayImages.map((imageUrl, index) => (
        <Box
          key={index}
          sx={{
            width: "100%",
            height: "100%",
            overflow: "hidden",
            backgroundColor: "#f0f0f0",
          }}
        >
          <img
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              objectPosition: "center",
            }}
            src={imageUrl}
            loading="lazy"
            alt={`Product image ${index + 1}`}
          />
        </Box>
      ))}
    </Box>
  );
};
